// RinkView — assembles the static rink scene: ice (with planar reflector),
// glass boards, neon trim rails, goal frames, and the starfield backdrop.
// Everything is built once; per-frame visual change happens through
// uniforms (style edits, FX pulses), never through geometry or material
// rebuilds.
//
// Dev HMR: this module is the direct importer of the material modules, so
// the `import.meta.hot.accept` handlers live at the bottom (Vite requires
// literal dep strings in the accepting importer). Editing a material file
// rebuilds just that material and repoints the mesh — game state, camera,
// and the rest of the scene are untouched.

import { reflector } from "three/tsl";
import { Group, type Material, Mesh } from "three/webgpu";
import {
  HOME_GOAL_Z,
  buildBoardWallGeometry,
  buildGoalFrameGeometry,
  buildIceGeometry,
  buildTrimRailsGeometry,
} from "../geometry/rink-geometry.js";
import {
  type GlassBoardsUniforms,
  applyGlassBoardsStyle,
  createGlassBoardsMaterial,
} from "../materials/glass-boards.js";
import {
  type GoalFrameUniforms,
  applyGoalFrameStyle,
  createGoalFrameMaterial,
} from "../materials/goal-frame.js";
import { type IceUniforms, applyIceStyle, createIceMaterial } from "../materials/ice.js";
import {
  type NeonTrimUniforms,
  applyNeonTrimStyle,
  createNeonTrimMaterial,
} from "../materials/neon-trim.js";
import {
  type StarfieldUniforms,
  applyStarfieldStyle,
  createStarfield,
} from "../materials/starfield.js";
import {
  type BoardsStyle,
  type IceStyle,
  type SpaceStyle,
  boardsStyle,
  iceStyle,
  spaceStyle,
} from "../style/style-schema.js";

type IceModule = typeof import("../materials/ice.js");
type GlassModule = typeof import("../materials/glass-boards.js");
type TrimModule = typeof import("../materials/neon-trim.js");
type GoalModule = typeof import("../materials/goal-frame.js");

export class RinkView {
  readonly root: Group;
  ice: { mesh: Mesh; u: IceUniforms };
  boards: { mesh: Mesh; u: GlassBoardsUniforms };
  trim: { mesh: Mesh; u: NeonTrimUniforms };
  goals: {
    home: { mesh: Mesh; u: GoalFrameUniforms };
    away: { mesh: Mesh; u: GoalFrameUniforms };
  };
  readonly stars: StarfieldUniforms;
  // The reflector output node — captured so material hot-swaps can rebuild
  // the ice material against the same reflection target.
  private readonly reflection: ReturnType<typeof reflector>;

  constructor() {
    this.root = new Group();

    // Ice + reflector. The reflector target renders the mirrored scene at
    // reduced resolution; its output node feeds the ice material.
    this.reflection = reflector({ resolutionScale: 0.25 });
    this.reflection.target.rotateX(-Math.PI / 2);
    this.root.add(this.reflection.target);

    const iceResult = createIceMaterial(iceStyle, this.reflection);
    const iceMesh = new Mesh(buildIceGeometry(), iceResult.material);
    this.root.add(iceMesh);
    this.ice = { mesh: iceMesh, u: iceResult.u };

    // Glass boards — rendered last (sample the viewport behind them).
    const glassResult = createGlassBoardsMaterial(boardsStyle);
    const boardsMesh = new Mesh(
      buildBoardWallGeometry(boardsStyle.boardHeight),
      glassResult.material,
    );
    boardsMesh.renderOrder = 10;
    this.root.add(boardsMesh);
    this.boards = { mesh: boardsMesh, u: glassResult.u };

    // Neon trim rails.
    const trimResult = createNeonTrimMaterial(boardsStyle);
    const trimMesh = new Mesh(
      buildTrimRailsGeometry(boardsStyle.boardHeight, boardsStyle.trim.railRadius),
      trimResult.material,
    );
    this.root.add(trimMesh);
    this.trim = { mesh: trimMesh, u: trimResult.u };

    // Goal frames: home at +z, away mirrored at -z.
    const goalGeo = buildGoalFrameGeometry(boardsStyle.goal.frameRadius);
    const homeResult = createGoalFrameMaterial(boardsStyle, "home");
    const homeGoal = new Mesh(goalGeo, homeResult.material);
    homeGoal.position.z = HOME_GOAL_Z;
    this.root.add(homeGoal);
    const awayResult = createGoalFrameMaterial(boardsStyle, "away");
    const awayGoal = new Mesh(goalGeo, awayResult.material);
    awayGoal.position.z = -HOME_GOAL_Z;
    awayGoal.rotation.y = Math.PI;
    this.root.add(awayGoal);
    this.goals = {
      home: { mesh: homeGoal, u: homeResult.u },
      away: { mesh: awayGoal, u: awayResult.u },
    };

    // Starfield backdrop.
    const stars = createStarfield(spaceStyle);
    this.root.add(stars.points);
    this.stars = stars.u;

    activeRinkView = this;
  }

  /** Re-apply style JSON payloads to live uniforms (lab sliders / JSON HMR). */
  applyIce(s: IceStyle): void {
    applyIceStyle(this.ice.u, s);
  }

  applyBoards(s: BoardsStyle): void {
    applyGlassBoardsStyle(this.boards.u, s);
    applyNeonTrimStyle(this.trim.u, s);
    applyGoalFrameStyle(this.goals.home.u, s, "home");
    applyGoalFrameStyle(this.goals.away.u, s, "away");
  }

  applySpace(s: SpaceStyle): void {
    applyStarfieldStyle(this.stars, s);
  }

  // --- Dev material hot-swap targets --------------------------------------

  hotSwapIce(mod: IceModule): void {
    const { material, u } = mod.createIceMaterial(iceStyle, this.reflection);
    repoint(this.ice.mesh, material);
    this.ice.u = u;
  }

  hotSwapGlass(mod: GlassModule): void {
    const { material, u } = mod.createGlassBoardsMaterial(boardsStyle);
    repoint(this.boards.mesh, material);
    this.boards.u = u;
  }

  hotSwapTrim(mod: TrimModule): void {
    const { material, u } = mod.createNeonTrimMaterial(boardsStyle);
    repoint(this.trim.mesh, material);
    this.trim.u = u;
  }

  hotSwapGoals(mod: GoalModule): void {
    for (const team of ["home", "away"] as const) {
      const { material, u } = mod.createGoalFrameMaterial(boardsStyle, team);
      repoint(this.goals[team].mesh, material);
      this.goals[team].u = u;
    }
  }
}

function repoint(mesh: Mesh, material: Material): void {
  const old = mesh.material as Material;
  mesh.material = material;
  old.dispose();
}

// One live RinkView per page (game or lab); module-scope handle so the HMR
// accept callbacks below can reach it.
let activeRinkView: RinkView | null = null;

if (import.meta.hot) {
  import.meta.hot.accept("../materials/ice.ts", (m) => {
    if (m !== undefined) activeRinkView?.hotSwapIce(m as unknown as IceModule);
  });
  import.meta.hot.accept("../materials/glass-boards.ts", (m) => {
    if (m !== undefined) activeRinkView?.hotSwapGlass(m as unknown as GlassModule);
  });
  import.meta.hot.accept("../materials/neon-trim.ts", (m) => {
    if (m !== undefined) activeRinkView?.hotSwapTrim(m as unknown as TrimModule);
  });
  import.meta.hot.accept("../materials/goal-frame.ts", (m) => {
    if (m !== undefined) activeRinkView?.hotSwapGoals(m as unknown as GoalModule);
  });
}
